import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Settings,
  BarChart3,
  Calendar,
  Webhook,
  Database,
  Zap,
  Users,
  Shield,
  TrendingUp,
  Menu,
  X
} from 'lucide-react';

const Sidebar = ({ isOpen, onToggle }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    manage: true,
    optimize: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const menuSections = [
    {
      id: 'manage',
      title: 'Manage',
      icon: Settings,
      items: [
        {
          title: 'Evaluations',
          path: '/evaluations',
          icon: BarChart3,
          description: 'View and manage evaluations'
        },
        {
          title: 'Templates',
          path: '/templates',
          icon: FileText,
          description: 'Manage evaluation templates'
        },
        {
          title: 'Schedules',
          path: '/evaluations/scheduling',
          icon: Calendar,
          description: 'Automated evaluation schedules'
        },
        {
          title: 'API Configuration',
          path: '/evaluations/api-config',
          icon: Zap,
          description: 'Configure LLM provider credentials'
        },
        {
          title: 'Webhooks',
          path: '/evaluations/webhooks',
          icon: Webhook,
          description: 'Manage webhook notifications'
        }
      ]
    },
    {
      id: 'optimize',
      title: 'Optimize',
      icon: TrendingUp,
      items: [
        {
          title: 'Results Dashboard',
          path: '/evaluations/results',
          icon: BarChart3,
          description: 'View evaluation results and analytics'
        },
        {
          title: 'Model Comparison',
          path: '/model-comparisons',
          icon: Database,
          description: 'Compare model performance'
        }
      ]
    }
  ];

  // Add admin section if user has admin privileges
  if (user?.role === 'super_admin' || user?.role === 'admin_staff') {
    menuSections.push({
      id: 'admin',
      title: 'Admin',
      icon: Shield,
      items: [
        {
          title: 'Default Templates',
          path: '/admin/templates',
          icon: FileText,
          description: 'Manage system-wide templates'
        },
        {
          title: 'User Management',
          path: '/admin/users',
          icon: Users,
          description: 'Manage user accounts'
        },
        {
          title: 'System Analytics',
          path: '/admin/analytics',
          icon: BarChart3,
          description: 'System-wide analytics'
        }
      ]
    });
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Evaluations</h2>
        <button
          onClick={onToggle}
          className="lg:hidden p-1 rounded-md hover:bg-accent transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-2">
        {menuSections.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded = expandedSections[section.id];

          return (
            <div key={section.id} className="space-y-1">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-all duration-200"
              >
                <div className="flex items-center space-x-2">
                  <SectionIcon className="w-4 h-4" />
                  <span>{section.title}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 transition-transform duration-200" />
                ) : (
                  <ChevronRight className="w-4 h-4 transition-transform duration-200" />
                )}
              </button>

              {/* Section Items */}
              <div className={`space-y-1 transition-all duration-200 overflow-hidden ${
                isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                {section.items.map((item) => {
                  const ItemIcon = item.icon;
                  const active = isActive(item.path);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`group flex items-center space-x-3 p-3 ml-6 rounded-md transition-all duration-200 ${
                        active
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      <ItemIcon className={`w-4 h-4 transition-colors ${
                        active ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${
                          active ? 'text-primary-foreground' : 'text-foreground'
                        }`}>
                          {item.title}
                        </div>
                        <div className={`text-xs truncate ${
                          active ? 'text-primary-foreground/80' : 'text-muted-foreground'
                        }`}>
                          {item.description}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Footer removed per request */}
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-80 bg-card border-r border-border shadow-lg
        transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:shadow-none
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <SidebarContent />
      </aside>

      {/* Mobile Toggle Button */}
      <button
        onClick={onToggle}
        className="fixed top-4 left-4 z-30 lg:hidden p-2 bg-card border border-border rounded-md shadow-md hover:bg-accent transition-colors"
      >
        <Menu className="w-5 h-5" />
      </button>
    </>
  );
};

export default Sidebar;
